import React, { useState, useEffect } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { DollarSign, Activity, Zap, TrendingUp } from 'lucide-react';
import { adminAPI } from '../services/api';

function ObservabilityDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await adminAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Error:', error);
        // Set demo stats
        setStats({
          campaigns: 8,
          recommendations: 12,
          total_llm_calls: 45,
          total_tokens: 125000,
          total_cost: 2.35
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Mock token usage data
  const tokenUsageData = [
    { name: 'Effectiveness Agent', tokens: 35000, cost: 0.70 },
    { name: 'Scenario Agent', tokens: 42000, cost: 0.84 },
    { name: 'Creative Agent', tokens: 28000, cost: 0.56 },
    { name: 'Data Connector', tokens: 12000, cost: 0.24 },
    { name: 'Monitor Agent', tokens: 8000, cost: 0.16 }
  ];

  const costByDay = [
    { day: 'Mon', cost: 1.2 },
    { day: 'Tue', cost: 2.4 },
    { day: 'Wed', cost: 1.8 },
    { day: 'Thu', cost: 2.1 },
    { day: 'Fri', cost: 1.6 },
    { day: 'Sat', cost: 0.8 },
    { day: 'Sun', cost: 0.9 }
  ];

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];

  if (loading) {
    return (
      <div className="card">
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Observability & Cost Tracking</h3>
        <p className="card-subtitle">Monitor AI usage, performance, and costs</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4" style={{ gap: '16px', marginBottom: '24px' }}>
        <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Activity size={16} style={{ color: 'var(--primary-500)' }} />
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>LLM Calls</p>
          </div>
          <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>{stats?.total_llm_calls || 45}</p>
        </div>
        <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Zap size={16} style={{ color: 'var(--accent-500)' }} />
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Total Tokens</p>
          </div>
          <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>{(stats?.total_tokens || 125000).toLocaleString()}</p>
        </div>
        <div style={{ padding: '16px', background: 'hsl(142, 76%, 96%)', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <DollarSign size={16} style={{ color: 'var(--success-600)' }} />
            <p style={{ fontSize: '0.75rem', color: 'var(--success-600)' }}>Total Cost</p>
          </div>
          <p style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--success-600)' }}>
            ${(stats?.total_cost || 2.35).toFixed(2)}
          </p>
        </div>
        <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <TrendingUp size={16} style={{ color: 'var(--info-500)' }} />
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Avg Latency</p>
          </div>
          <p style={{ fontSize: '1.75rem', fontWeight: '700' }}>1.2s</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2" style={{ gap: '24px' }}>
        {/* Token Usage by Agent */}
        <div>
          <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px' }}>Token Usage by Agent</h4>
          <div style={{ height: '250px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={tokenUsageData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name.split(' ')[0]}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="tokens"
                >
                  {tokenUsageData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value.toLocaleString()} tokens`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Cost Trend */}
        <div>
          <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '16px' }}>Daily Cost Trend</h4>
          <div style={{ height: '250px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={costByDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                <XAxis dataKey="day" style={{ fontSize: '12px' }} />
                <YAxis style={{ fontSize: '12px' }} />
                <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                <Bar dataKey="cost" fill="var(--success-500)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div style={{ marginTop: '24px' }}>
        <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px' }}>Cost Breakdown by Agent</h4>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-light)', textAlign: 'left' }}>
                <th style={{ padding: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Agent</th>
                <th style={{ padding: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Tokens</th>
                <th style={{ padding: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Cost</th>
                <th style={{ padding: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>% of Total</th>
              </tr>
            </thead>
            <tbody>
              {tokenUsageData.map((agent, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '12px', fontWeight: '500' }}>{agent.name}</td>
                  <td style={{ padding: '12px' }}>{agent.tokens.toLocaleString()}</td>
                  <td style={{ padding: '12px', fontWeight: '600', color: 'var(--success-600)' }}>${agent.cost.toFixed(2)}</td>
                  <td style={{ padding: '12px' }}>
                    {((agent.cost / 2.5) * 100).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ObservabilityDashboard;
